import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import LinearProgress from "@mui/material/LinearProgress";
import Link from "@mui/material/Link";
import useMutation from "src/hooks/useMutation";
import {
  PROJECT_BULK_CREATE_IMAGES_MUTATION,
  PROJECT_REMOVE_IMAGE_MUTATION,
} from "src/constants/graphql/mutations";
import { useProject } from "../ProjectProvider";
import CardEditable from "../../card/CardEditable";
import ConfirmDeleteModal from "../../../components/images/ConfirmDeleteModal";
import AttachmentViewerModal from "./AttachmentViewerModal";
import { serverStrftime } from "src/utils/dates";

export default function ProjectAttachmentsCompact() {
  const { project, refetch } = useProject();
  const attachments = project?.images || [];
  const [addAttachments, { loading: saving }] = useMutation(PROJECT_BULK_CREATE_IMAGES_MUTATION);
  const [removeAttachment, { loading: removing }] = useMutation(PROJECT_REMOVE_IMAGE_MUTATION);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const onDrop = useCallback(
    acceptedFiles => {
      addAttachments({
        variables: {
          projectId: project.id,
          projectImages: acceptedFiles,
        },
        onSuccess: () => {
          refetch();
        },
      });
    },
    [project?.id, addAttachments, refetch]
  );

  const { getRootProps, getInputProps } = useDropzone({ onDrop, noClick: true });
  const handleDeleteClick = attachment => {
    setAttachmentToDelete(attachment);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (attachmentToDelete) {
      removeAttachment({
        variables: { id: attachmentToDelete.id },
        onSuccess: () => {
          refetch();
          setOpenDeleteModal(false);
          setAttachmentToDelete(null);
        },
      });
    }
  };

  const handleAddClick = () => {
    document.getElementById("attachment-file-input").click();
  };

  const handleOpenViewer = index => {
    setViewerIndex(index);
    setViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
  };

  return (
    <Box mt={3}>
      <Box height={16} pt={0.75}>
        {(saving || removing) && <LinearProgress />}
      </Box>
      <Typography fontWeight={400} mb={2}>
        Attachments
      </Typography>
      <div {...getRootProps()}>
        <input {...getInputProps()} id="attachment-file-input" />
        <Grid container spacing={1}>
          {attachments.map((attachment, index) => (
            <Grid key={index} size={{ xs: 12, sm: 6 }}>
              <CardEditable
                title={
                  <Link
                    component="button"
                    onClick={() => handleOpenViewer(index)}
                    sx={{ textAlign: "left" }}
                  >
                    {attachment.fileName || "No name"}
                  </Link>
                }
                onDelete={() => handleDeleteClick(attachment)}
              >
                <Typography variant="caption" display="block" color="text.secondary">
                  {serverStrftime(attachment.createdAt)}
                </Typography>
              </CardEditable>
            </Grid>
          ))}
          <Grid size={12}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddClick}>
              {attachments.length === 0 ? "Add Attachments" : "Add Another Attachment"}
            </Button>
          </Grid>
        </Grid>
      </div>
      {openDeleteModal && (
        <ConfirmDeleteModal
          title="Delete Attachment"
          descr="Are you sure you want to delete this attachment?"
          onClose={() => setOpenDeleteModal(false)}
          onDelete={handleConfirmDelete}
        />
      )}
      <AttachmentViewerModal
        open={viewerOpen}
        onClose={handleCloseViewer}
        attachments={attachments}
        initialIndex={viewerIndex}
      />
    </Box>
  );
}
